import {FC, useState} from 'react'

import {Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {CheckCircleIcon} from '@/components/common/icons/CheckCircleIcon'
import {Modal} from '@/components/common/modal/Modal'
import {useTrademarkVerificationContent} from '@/content/trademarkVerification'
import {Trans, useTranslation} from '@/i18n'
import {FORM_MODAL_WIDTH} from '@/theme/sizing'
import {theme} from '@/theme/theme'
import {USER_TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'
import {analytics} from '@/utils/tracking/analytics'

import {Alert} from '../alerts/Alert'

type IncludedPriceBenefitsProps = {
  description: string
  benefitsTitle?: string
  benefitsList?: {
    title: string
    description: string
  }[]
  textVariant?: TextVariant
  color?: string
  linkColor?: string
  disclaimer?: string | null
}

export const IncludedPriceBenefits: FC<React.PropsWithChildren<IncludedPriceBenefitsProps>> = ({
  description,
  benefitsTitle,
  benefitsList,
  textVariant = TextVariant.text14,
  color = theme.palette.text.default,
  linkColor = color ?? theme.palette.info.main,
  disclaimer,
}) => {
  const {t} = useTranslation()

  const {markets} = useTrademarkVerificationContent()
  const [isModalVisible, setIsModalVisible] = useState(false)

  const openModal = () => {
    setIsModalVisible(true)
    analytics.track('Modal - What is included in price? - is open')
  }

  const closeModal = () => {
    setIsModalVisible(false)
    analytics.track('Modal - What is included in price? - is closed')
  }

  const title = benefitsTitle || t(markets.benefits.title)
  const benefits = benefitsList || markets.benefits.list
  const disclaimerText =
    disclaimer === undefined ? t(markets.benefits.filingFeesDisclaimer) : disclaimer

  return (
    <>
      <Text variant={textVariant} fontWeight={TextFontWeight.regular} element="span" color={color}>
        <Trans i18nKey={description}>
          text
          <span className="underline" onClick={() => openModal()} role="button" tabIndex={0}>
            text
          </span>
          text
        </Trans>
      </Text>
      <Modal isVisible={isModalVisible} closeModal={() => closeModal()} isMobileFullScreen>
        <div className="wrapper">
          <Text variant={TextVariant.h3} element="h2" align="center">
            {title}
          </Text>
          <ul>
            {benefits.map(({title, description}) => (
              <li key={title} className="item">
                <div className="icon">
                  <CheckCircleIcon />
                </div>
                <div>
                  <Text align="left" fontWeight={TextFontWeight.medium}>
                    {t(title)}
                  </Text>
                  <Text variant={TextVariant.text14} color={theme.palette.text.light} align="left">
                    {t(description)}
                  </Text>
                </div>
              </li>
            ))}
          </ul>
          {disclaimerText && <Alert description={disclaimerText} />}
        </div>
      </Modal>

      <style jsx>{`
        .underline {
          color: ${linkColor};
          text-decoration: underline;
          cursor: pointer;
        }
        .wrapper {
          display: flex;
          flex-direction: column;
          gap: ${theme.spacing[5]};
          max-width: calc(${FORM_MODAL_WIDTH} - ${theme.spacing[6]} * 2);
          margin: 0 auto;
          padding: ${theme.spacing[3]} ${theme.spacing[8]};
        }
        .item {
          display: flex;
        }
        .item + .item {
          margin-top: ${theme.spacing[3]};
        }
        .icon {
          margin-top: calc(${theme.spacing[1]} / 2);
          margin-right: 12px;
        }
        @media ${USER_TABLET_SCREENSIZE} {
          .wrapper {
            padding: ${theme.spacing[3]} 0;
          }
        }
      `}</style>
    </>
  )
}
